import {Box, Grid} from '@mui/material';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import {t} from 'i18next';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {
  keywordHighlightForm,
  keywordHighlightSchema,
} from './AddEditKeywordModalUtils';
import {zodResolver} from '@hookform/resolvers/zod';
import './AddEditKeywordModal.scss';
import ArrTextArea from '../../common/arr-text-area/ArrTextArea';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {COLOR_MAP, ToastType} from '../../../shared/constants';
import {omitBy, isNil} from 'lodash';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {
  useAddKeywordHighlight,
  useGetKeywordHighlightDetails,
  useKeywordDropdownColorList,
} from '../../../hooks/document/useDocument';
import {
  IKeywordSavePayload,
  IKeywordSaveResponse,
} from '../../../api/types/document.type';
import {ISelectItem} from '../../../shared/types';

export type AddEditKeywordHightlightdModalRef = {
  open: (id?: string | number) => void;
  close: () => void;
};

interface AddEditProjectFieldModalProps {
  onSaveSuccess?: () => void;
  showModal?: boolean;
  setShowModal?: (show: boolean) => void;
}

const AddEditKeywordHighlightModal = forwardRef<
  AddEditKeywordHightlightdModalRef,
  AddEditProjectFieldModalProps | null
>((props, ref) => {
  const {
    showModal = false,
    setShowModal = () => {},
    onSaveSuccess,
  } = props || {};
  const [isShowSuccessToast, setIsShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isShowErrorToast, setIsShowErrorToast] = useState(false);
  const [isShowWarningToast, setIsShowWarningToast] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState(false);
  const [keywordId, setKeywordId] = useState<
    string | number | undefined | null
  >(undefined);

  /** ------------ Form ------------ **/
  const {
    control,
    register,
    reset,
    setValue,
    formState: {errors, isValid},
  } = useForm<keywordHighlightForm>({
    resolver: zodResolver(keywordHighlightSchema()),
    mode: 'onChange',
    defaultValues: {
      keywordGrpName: '',
      description: '',
      keywords: '',
      color: '',
    },
  });

  const [keywordGrpName, description, keywords] = useWatch({
    control,
    name: ['keywordGrpName', 'description', 'keywords'],
  });

  const [selectedColor, setSelectedColor] = useState<ISelectItem | null>(null);

  const addKeywordMutation = useAddKeywordHighlight();
  const {mutate: saveKeyword, reset: resetSaveKeyword} = addKeywordMutation;

  const loading = useMemo(
    () => addKeywordMutation.isPending,
    [addKeywordMutation.isPending]
  );

  useEffect(() => {
    if (addKeywordMutation.isError) {
      setIsShowErrorToast(true);
    }
  }, [addKeywordMutation.isError]);

  const errorMessage = useMemo(
    () => (addKeywordMutation.error?.message as any)?.detail?.split('\n')?.[0],
    [addKeywordMutation.error?.message]
  );

  const onClose = useCallback(() => {
    setShowModal(false);
    reset();
  }, [reset]);

  useEffect(() => {
    if (addKeywordMutation.isSuccess) {
      const responseData =
        addKeywordMutation.data as unknown as IKeywordSaveResponse;
      if (
        responseData.message ==
        t('keywordHighlighting.addEditKeyword.nameAlreadyExist')
      ) {
        setConfirmModal(false);
        setIsShowWarningToast(true);
        setWarningMessage(responseData.message);
      } else {
        onClose();
        setSuccessMessage(
          keywordId
            ? t('keywordHighlighting.addEditKeyword.editSuccessMessage')
            : t('keywordHighlighting.addEditKeyword.addSuccessMessage')
        );
        setShowModal(false);
        setConfirmModal(false);
        setIsShowSuccessToast(true);
        addKeywordMutation.reset();
        onSaveSuccess?.();
      }
    }
  }, [addKeywordMutation.isSuccess, onClose, onSaveSuccess]);

  /** ------------ Fetch Colors ------------ **/
  const {data: keywordHighlightList} = useKeywordDropdownColorList();

  const rawColorList = useMemo(() => {
    return (
      keywordHighlightList?.keyWordColorList?.map((c) => ({
        id: c.value,
        name: c.text,
        apiColor: c.value,
        isSelected: c.isSelected,
      })) ?? []
    );
  }, [keywordHighlightList?.keyWordColorList]);

  /**Apply color mapping */
  const colorList = useMemo(() => {
    return rawColorList.map((item) => ({
      ...item,
      color: COLOR_MAP[item.name] || '#000000',
    }));
  }, [rawColorList]);

  useEffect(() => {
    if (!colorList.length) return;
    const preselected = colorList.find((c) => c.isSelected);
    const defaultItem = preselected ?? colorList[0];
    setSelectedColor(defaultItem);
    setValue('color', defaultItem.id);
  }, [colorList, setValue]);

  const handleColorChange = (item: ISelectItem | null) => {
    if (item) {
      setSelectedColor(item);
      setValue('color', String(item.id), {shouldValidate: true});
    }
  };

  const {data, isLoading, refetch} = useGetKeywordHighlightDetails(
    keywordId ?? null
  );

  useImperativeHandle(ref, () => ({
    open: (id?: number | string) => {
      setKeywordId(id ?? null);
      setShowModal(true);
      if (id) {
        refetch();
      } else {
        setSelectedColor(colorList[0]);
        setValue('color', colorList[0].id);
      }
    },
    close: onClose,
  }));

  const handleSave = () => {
    setConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setConfirmModal(false);
  };

  useEffect(() => {
    if (!data?.keyWordData) return;

    const d = data.keyWordData;
    reset({
      keywordGrpName: d.keywordName ?? '',
      description: d.description ?? '',
      keywords: d.keywords ?? '',
      color: d.colorCode ?? '',
    });

    const matched = colorList.find((c) => String(c.id) === String(d.colorCode));
    if (matched) {
      setSelectedColor(matched);
    } else if (colorList.length) {
      setSelectedColor(colorList[0]);
      setValue('color', colorList[0].id, {shouldValidate: true});
    }
  }, [data?.keyWordData]);

  const handleConfirmModal = useCallback(() => {
    const payload: IKeywordSavePayload = {
      keywordName: keywordGrpName,
      description: description,
      keywords: keywords,
      colorCode: selectedColor?.id ?? '',
      isKeywordEdited: !!keywordId,
    };

    if (keywordId) payload.keywordID = keywordId;
    const params = omitBy(payload, (v) => isNil(v) || v === '');
    resetSaveKeyword();
    saveKeyword(params);
  }, [
    keywordGrpName,
    description,
    keywords,
    selectedColor?.id,
    saveKeyword,
    resetSaveKeyword,
  ]);

  return (
    <div data-testid="add-keyword-highlight-modal">
      <ArrModal
        showModal={showModal}
        showClose={true}
        disabledClose={loading}
        onClose={onClose}
        title={
          keywordId
            ? t('keywordHighlighting.addEditKeyword.editTitle')
            : t('keywordHighlighting.addEditKeyword.addTitle')
        }
        className="keyword-highlight-modal"
        dataTestId="add-edit-keyword-modal"
        disableFocusTitle={true}
        buttons={[
          {
            id: 'cancel',
            label: t('common.cancel'),
            onClick: onClose,
            variant: 'secondary',
            size: 'medium',
            disabled: loading,
            dataTestId: 'aek-cancel-btn',
          },
          {
            id: 'save',
            label: t('common.save'),
            onClick: handleSave,
            variant: 'primary',
            size: 'medium',
            disabled: !isValid,
            dataTestId: 'aek-save-btn',
          },
        ]}
      >
        <Box className="keyword-highlight-input-content">
          <Grid container spacing={12}>
            <Grid size={12}>
              {/* Show Loader for all fields when editing */}
              {keywordId && isLoading ? (
                <div data-testid={`add-edit-keyword-modal-loading`}>
                  Loading...
                </div>
              ) : (
                <>
                  <ArrTextField
                    id="keywordGrpName"
                    state={errors.keywordGrpName ? 'error' : 'enabled'}
                    label={t(
                      'keywordHighlighting.addEditKeyword.keywordGrpName'
                    )}
                    dataTestId="keyword-group-name-input"
                    placeholder={t(
                      'keywordHighlighting.addEditKeyword.keywordGrpName'
                    )}
                    {...register('keywordGrpName')}
                    supportText={errors.keywordGrpName?.message || ''}
                  />

                  {/* Color Dropdown */}
                  <ArrSingleSelectDropdown
                    dropdownLabel={t(
                      'keywordHighlighting.addEditKeyword.color'
                    )}
                    dataTestId="asd-keyword-color-dropdown"
                    sizeVariant="large"
                    dropdownItems={colorList}
                    selectedValues={selectedColor}
                    menuPropClassName="keyword-modal-select"
                    onSelectionChange={handleColorChange}
                    renderPrefix={(item) => (
                      <span
                        className="color-dot"
                        style={{backgroundColor: item.color}}
                        data-testid={`add-edit-keyword-modal-span-${item.id}`}
                      />
                    )}
                    tabIndex={1}
                  />
                  {/* Description */}
                  <ArrTextField
                    id="description"
                    state={errors.description ? 'error' : 'enabled'}
                    label={t('keywordHighlighting.addEditKeyword.description')}
                    dataTestId="keyword-description-input"
                    placeholder={t(
                      'keywordHighlighting.addEditKeyword.description'
                    )}
                    maxLength={256}
                    {...register('description')}
                    supportText={errors.description?.message || ''}
                  />
                  {/* Keywords */}
                  <ArrTextArea
                    rows={4}
                    maxRows={80}
                    label={t('keywordHighlighting.addEditKeyword.keywords')}
                    id="keywords"
                    placeholder={t(
                      'keywordHighlighting.addEditKeyword.keywordPlaceholder'
                    )}
                    maxLength={4000}
                    value={keywords}
                    helperText={
                      errors.keywords?.message ||
                      t('keywordHighlighting.addEditKeyword.keywordSupportText')
                    }
                    dataTestId="edit-keyword-text-area"
                    error={!!errors.keywords}
                    onChange={(value) =>
                      setValue('keywords', value, {shouldValidate: true})
                    }
                  />
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </ArrModal>
      <ArrModal
        showModal={confirmModal}
        showClose={true}
        disableFocusTitle={true}
        onClose={closeConfirmModal}
        title={t('keywordHighlighting.addEditKeyword.saveKeyword')}
        className="keyword-add-edit-confirm-modal"
        dataTestId="keyword-add-edit-confirm-modal"
        buttons={[
          {
            id: 'confirm_cancel',
            label: t('common.cancel'),
            onClick: closeConfirmModal,
            variant: 'secondary',
            size: 'medium',
            dataTestId: 'kaec-cancel-btn',
          },
          {
            id: 'confirm_save',
            label: t('keywordHighlighting.addEditKeyword.ok'),
            onClick: handleConfirmModal,
            variant: 'primary',
            size: 'medium',
            loading,
            disabled: loading,
            dataTestId: 'kaec-save-btn',
          },
        ]}
      >
        <Box className="keyword-confirm-modal-content">
          <Grid
            container
            spacing={12}
            className="typo-text body2_size glacier-border-dim"
          >
            {t('keywordHighlighting.addEditKeyword.confirmModalContent')}
          </Grid>
        </Box>
      </ArrModal>
      {/* Success Toast */}
      {isShowSuccessToast && (
        <ArrToastNotification
          message={successMessage}
          status={[ToastType.Success]}
          onClose={() => setIsShowSuccessToast(false)}
        />
      )}
      {/* Error Toast */}
      {isShowErrorToast && (
        <ArrToastNotification
          message={errorMessage}
          status={[ToastType.Error]}
          onClose={() => setIsShowErrorToast(false)}
        />
      )}
      {/* Warning Toast */}
      {isShowWarningToast && (
        <ArrToastNotification
          message={warningMessage}
          status={[ToastType.Warning]}
          onClose={() => setIsShowWarningToast(false)}
        />
      )}
    </div>
  );
});

export {AddEditKeywordHighlightModal};
